import { Text, Flex, Link } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';
import { workPhaseMap } from '@publishing-engine/domain/models';
import { Button, Modal } from '@morressier/alembic';
import { openFile } from 'src/utils/file';
import NoteContextMenu from './NoteContextMenu';
import { useKeycloak } from 'src/components/AuthHOC';
import { useEffect, useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { format } from 'date-fns';
import { faEdit, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { searchJournals, searchUsers } from 'src/api/queries';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: ISubmissionNotesListItem;
  readOnly?: boolean;
}
// workPhaseMap doesn't have the copy for ALL the states, so using this util function
const getStateCopy = (state: string): string => {
  // @ts-ignore
  return workPhaseMap[state] || state;
};

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const ViewNoteModal: React.FC<Props> = ({
  isOpen,
  onClose,
  data,
  readOnly = false
}) => {
  const {
    fieldsResponse: { organizationId },
    submissionId
  } = useSubmissionData();
  const ctxId = useCurrentContextId() ?? '';
  const { currentUser } = useKeycloak();
  const userQuery = '';
  const journalQuery = '';
  const [editTrigger, setEditTrigger] = useState(false);
  const [deleteTrigger, setDeleteTrigger] = useState(false);

  const { data: users } = useQuery({
    queryKey: ['users-search', userQuery, { organizationId }],
    queryFn: () => searchUsers(userQuery, { organizationId })
  });

  const { data: journals } = useQuery({
    queryKey: ['journals-search', journalQuery, { organizationId }],
    queryFn: () => searchJournals(journalQuery, { organizationId })
  });

  const mappedUsers = users?.collection.map(user => ({
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    type: 'user' as const
  }));

  const mappedJournals = journals?.collection.map(journal => ({
    id: journal.id,
    name: journal.data.name,
    email: journal.config.email!,
    type: 'journal' as const
  }));

  useEffect(() => {
    if (editTrigger) setEditTrigger(false);
    if (deleteTrigger) setDeleteTrigger(false);
  }, [editTrigger, deleteTrigger]);

  const renderContentWithHighlights = (text: string) => {
    // Create the list of valid mentions
    const mentions = [
      ...(mappedUsers?.map(entity => `@${entity.name}`) || []),
      ...(mappedJournals?.map(hashtagEntity => `#${hashtagEntity.name}`) || [])
    ];

    const mentionRegex = new RegExp(
      `(${mentions.map(escapeRegExp).join('|')})`,
      'g'
    );

    const parts = text.split(mentionRegex);
    return parts.map((part, index) => {
      // Odd indices are the matched mentions
      if (index % 2 === 1) {
        return (
          <Text
            as="span"
            color="actionPrimaryContentInverse"
            fontWeight="600"
            key={`mention-${index}`}
          >
            {part}
          </Text>
        );
      }

      return part;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="700px"
      closeButtonProps={{
        backgroundColor: 'white'
      }}
    >
      <Flex flexDirection="column" padding={4}>
        <Text textStyle="headingGroup">View Note</Text>
      </Flex>
      <Flex flexDirection="column" padding={4}>
        <Flex flexDirection="column" gap={4}>
          {data?.title && (
            <Flex flexDirection="column">
              <Text textStyle="headingItem">Title</Text>
              <Text textStyle="textBody">{data?.title}</Text>
            </Flex>
          )}
          {data?.content && (
            <Flex flexDirection="column">
              <Text textStyle="headingItem">Note</Text>
              <Text textStyle="textBody">
                {renderContentWithHighlights(data?.content)}
              </Text>
            </Flex>
          )}
          <Flex
            flexDirection="row"
            align="flex-start"
            justify="space-between"
            gap={8}
            width="80%"
          >
            <Flex flexDirection="column">
              <Text textStyle="headingItem">User</Text>
              <Text textStyle="textBody">
                {`${data?.addedBy?.firstName} ${data?.addedBy?.lastName}`}
              </Text>
            </Flex>

            <Flex flexDirection="column">
              <Text textStyle="headingItem">Category</Text>
              <Text textStyle="textBody">{data?.category}</Text>
            </Flex>

            <Flex flexDirection="column">
              <Text textStyle="headingItem">Last Updated On</Text>
              <Text textStyle="textBody">
                {format(new Date(data?.modified_at), 'MMM dd, yyyy')}
              </Text>
            </Flex>
          </Flex>

          {data?.contextualInformation?.context === 'decision' && (
            <Flex flexDirection="column">
              <Text textStyle="headingItem">Phase Information</Text>
              <Text whiteSpace="pre-wrap" textStyle="textBody">
                Note saved during the transition from{' '}
                {getStateCopy(data?.contextualInformation.fromState)} to{' '}
                {getStateCopy(data?.contextualInformation.toState)}.
              </Text>
            </Flex>
          )}
          {data?.contextualInformation?.context === 'assessment' && (
            <Flex flexDirection="column">
              <Text textStyle="headingItem">Phase Information</Text>
              <Text whiteSpace="pre-wrap" textStyle="textBody">
                {data?.contextualInformation.checklistCategory} Note taken
                during {getStateCopy(data?.targetPhase)}.
              </Text>
            </Flex>
          )}
          {data?.attachments && data.attachments.length > 0 && (
            <Flex flexDirection="column" gap={0}>
              <Text textStyle="headingItem">Files</Text>
              {data.attachments.map((file, index) => (
                <Flex key={index} alignItems="center">
                  <Link
                    onClick={() => openFile(file.url)}
                    color="#2043DF"
                    textStyle="headingItem"
                    textDecoration="none"
                    _hover={{ textDecoration: 'underline' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {file?.name}
                  </Link>
                </Flex>
              ))}
            </Flex>
          )}
          {!readOnly && currentUser?.id === data?.userId && (
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
              mt={4}
            >
              <Button
                variant="secondary"
                backgroundColor="#FFFFFF"
                onClick={() => setDeleteTrigger(true)}
                borderRadius="small"
              >
                <FontAwesomeIcon icon={faTrashAlt} cursor="pointer" />
                Delete
              </Button>
              <Button
                variant="primary"
                type="submit"
                onClick={() => setEditTrigger(true)}
                borderRadius="small"
              >
                <FontAwesomeIcon icon={faEdit} cursor="pointer" />
                Edit
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
      {!readOnly && (
        <NoteContextMenu
          data={data}
          submissionId={submissionId}
          submissionContextId={ctxId}
          organizationId={organizationId}
          fetchNotesQuery={['submission-notes', { submissionId }]}
          externalEditTrigger={editTrigger}
          externalDeleteTrigger={deleteTrigger}
          onParentClose={onClose}
        />
      )}
    </Modal>
  );
};

export default ViewNoteModal;
